import React, {useState} from 'react'
import {NodeContact, PolicySummary} from '../../../types/contactsApi'
import {BadgeStatePolicy} from '../../onePolicy/components/BadgeStatePolicy'
import {getColorByStatus, getTitleByStatus} from '../../shared/utils'
import {PolicyStatus} from '../../../types/policies'
import {ModalPreview as ModalPreviewPolicy} from '../../policies/components/ModalPreview'

const EMPTY_DASH = '—'

interface PolicyTypeSummary {
  /** Nombre del tipo tal como viene del backend (ACA, Vision, Dental, …). */
  type: string
  policyId: string
  /** Año de vigencia de la póliza más reciente de ese tipo. */
  year: number | null
  carrier: string | null
  status: string | null
}

/**
 * Por cada tipo de póliza del contacto deja solo la más reciente (por fecha de
 * vigencia) y devuelve su año, carrier y estado. Los tipos se ordenan de más
 * reciente a más antiguo para que lo vigente quede arriba.
 */
export const summarizePolicyTypes = (contact: NodeContact): PolicyTypeSummary[] => {
  const policies: PolicySummary[] = []
  if (contact.memberPolicy?.policy) policies.push(contact.memberPolicy.policy)
  contact.members?.edges?.forEach(({node}) => {
    if (node.policy) policies.push(node.policy)
  })

  const latestByType = new Map<string, PolicySummary>()
  const seenPolicyIds = new Set<string>()
  policies.forEach((policy) => {
    const type = policy.typePolicy?.typePolicy?.trim()
    if (!type || seenPolicyIds.has(policy.id)) return
    seenPolicyIds.add(policy.id)
    const current = latestByType.get(type)
    const isNewer =
      !current ||
      `${policy.effectiveDate ?? ''}`.localeCompare(`${current.effectiveDate ?? ''}`) > 0
    if (isNewer) latestByType.set(type, policy)
  })

  return Array.from(latestByType.entries())
    .map(([type, policy]) => ({
      type,
      policyId: policy.id,
      year: policy.effectiveDate ? new Date(policy.effectiveDate).getUTCFullYear() : null,
      carrier: policy.carrier?.name ?? null,
      status: policy.status,
    }))
    .sort((a, b) => (b.year ?? 0) - (a.year ?? 0) || a.type.localeCompare(b.type))
}

interface ContactPolicyTypesProps {
  contact: NodeContact
  statuses: {node: PolicyStatus}[]
}

/**
 * Columna "Latest Policies" del listado de contactos: tipo · año · carrier · estado.
 * El carrier abre el preview de esa póliza — el mismo modal del listado de pólizas.
 */
export const ContactPolicyTypes: React.FC<ContactPolicyTypesProps> = ({contact, statuses}) => {
  const [previewPolicy, setPreviewPolicy] = useState<null | {id: string; status: string | null}>(
    null
  )
  const summaries = summarizePolicyTypes(contact)
  if (summaries.length === 0) return <span className='text-muted'>{EMPTY_DASH}</span>

  return (
    <>
      <div className='d-flex flex-column gap-1'>
        {summaries.map(({type, policyId, year, carrier, status}) => (
          <div key={type} className='d-flex align-items-center gap-2' style={{fontSize: '0.85rem'}}>
            <span className='fw-bold'>{type}</span>
            {year !== null && <span className='text-muted'>{year}</span>}
            {status && (
              <BadgeStatePolicy
                status={getTitleByStatus(statuses, status)}
                color={getColorByStatus(statuses, status)}
              />
            )}
            {/* El carrier va último y sin truncar: su nombre puede ser largo. */}
            {carrier && (
              <span
                className='text-primary text-link fw-bold'
                style={{cursor: 'pointer'}}
                onClick={(e) => {
                  // La fila entera abre el perfil del contacto; aquí queremos la póliza.
                  e.stopPropagation()
                  setPreviewPolicy({id: policyId, status})
                }}
              >
                {carrier}
              </span>
            )}
          </div>
        ))}
      </div>

      {previewPolicy && (
        // El modal se monta dentro de la fila: sin este stopPropagation, cerrarlo o
        // interactuar con él dispararía el onClick de la fila (perfil del contacto).
        <div onClick={(e) => e.stopPropagation()}>
          <ModalPreviewPolicy
            id={previewPolicy.id}
            isOpen={true}
            onClose={() => setPreviewPolicy(null)}
            statusColor={
              previewPolicy.status ? getColorByStatus(statuses, previewPolicy.status) : undefined
            }
          />
        </div>
      )}
    </>
  )
}
